import { ApiError } from "@/lib/api";
import type { RecordAutodeskPush } from "@/types/api";

const IN_FLIGHT = new Set(["pending", "creating", "attaching"]);

/**
 * What a record's Autodesk push row means, as one word.
 *
 * Lives in its own module because two components read it: the menu row itself
 * ([[autodesk-push-menu-item.spec]]) and the status dot on the closed Share
 * trigger ([[record-share-menu.spec]]). One mapping means the dot and the row
 * can never disagree about the same record.
 *
 * `"hidden"` is the "this viewer may not push at all" answer (403) — distinct
 * from `"none"`, the ordinary never-pushed state.
 */
export type AutodeskPushIndicator =
  | "hidden"
  | "loading"
  | "none"
  | "sending"
  | "linked"
  | "failed";

export function autodeskPushIndicator(
  push: RecordAutodeskPush | null | undefined,
  error: unknown,
): AutodeskPushIndicator {
  // A 403 means this viewer can't see the record's push state at all. Offering
  // the action would just walk them into the same 403 on tap.
  if (error instanceof ApiError && error.status === 403) return "hidden";
  // `undefined` is both "GET still in flight" and "non-403 query error"; only a
  // confirmed `null` (the hook's caught 404) means never-pushed. Anything else
  // must not present a tappable Send, or an already-completed record would
  // offer a redundant push for the length of the initial fetch.
  if (push === undefined) return "loading";
  const status = push?.status;
  if (status && IN_FLIGHT.has(status)) return "sending";
  if (status === "completed") return "linked";
  if (status === "failed") return "failed";
  return "none";
}
